import AnimationComponent from '../Component/Animation';
import TextComponent from '../Component/Render/Text';
import Renderable from '../Component/Renderable';
import TransformComponent from '../Component/Transform';
import Game from '../Game';
import GameObject from '../GameObject/GameObject';
import Arrow from '../Prototype/Level/Arrow';
import Text from '../Prototype/Text';
import CardStateHelper, {
    getGoldCardsInPlay,
} from '../State/Card/CardStateHelper';
import { ILevelStateTaskData } from '../State/Level/ILevelState';
import { LevelTask } from '../State/Level/LevelTask';
import Point from '../Utility/Point';
import { FBTrackingEvent } from '../Utility/Tracking/FBTrackingEvent';
import { StartAnimation } from './Animation/AnimationHelper';
import CardSystem from './Card/CardSystem';
import { ICoroutine } from './System';
import { PlayerActions } from '../State/Player/PlayerActions';
import PIXIRenderer from './Render/PIXIRenderer';
import { RenderSystemEvent } from './Render/RenderSystem';

export default class LevelTaskHelper {
    public static routineMap: Map<LevelTask, ICoroutine> = new Map();

    public static handleTask(
        system: CardSystem,
        taskData: ILevelStateTaskData
    ): void {
        // prettier-ignore
        switch (taskData.task) {
            case LevelTask.FTUX_CARD_MATCH:
                return this.startRoutine(system, taskData.task, this.cardMatch(system));
            case LevelTask.FTUX_CARD_DECK:
                return this.startRoutine(system, taskData.task, this.cardDeck(system));
            case LevelTask.FTUX_BONUS_STREAK:
                return this.startRoutine(system, taskData.task, this.bonusStreak(system));
            case LevelTask.FTUX_BOOSTER_UNDO:
                return this.startRoutine(system, taskData.task, this.undoBooster(system));
            case LevelTask.FTUX_BOOSTER_JOKER:
                return this.startRoutine(system, taskData.task, this.jokerBooster(system));
            case LevelTask.FTUX_BOOSTER_TNT:
                return this.startRoutine(system, taskData.task, this.suitcaseFlow(system, 1));
            case LevelTask.FTUX_BOOSTER_BULLS_EYE:
                return this.startRoutine(system, taskData.task, this.suitcaseFlow(system, 2));
            case LevelTask.FTUX_BOOSTER_SHUFFLE:
                return this.startRoutine(system, taskData.task, this.suitcaseFlow(system, 3));
            case LevelTask.FTUX_SUITCASE:
                return;
            case LevelTask.FTUX_HAZARD_BOMB:
                return this.startRoutine(system, taskData.task, this.bombCard(system));
            case LevelTask.FTUX_GOLD_CARDS:
                return this.startRoutine(system, taskData.task, this.goldCards(system));
        }
    }

    private static startRoutine(
        system: CardSystem,
        task: LevelTask,
        iterable: IterableIterator<number>
    ) {
        const routine = LevelTaskHelper.routineMap.get(task);
        if (routine && system.coroutines.indexOf(routine) !== -1) return;

        LevelTaskHelper.routineMap.set(task, system.startCoroutine(iterable));
    }

    private static *cardMatch(system: CardSystem): IterableIterator<number> {
        Game.sdk.gameTracking(FBTrackingEvent.FTUE_05_FirstCardClear);

        system.animator.addChild(system.cardsContainer);
        system.animator.addChild(system.stackOuterContainer);

        // Fix relative positions after re-parenting.
        system.cardsContainer
            .getComponent(TransformComponent)
            .position.set(0, 0);
        system.stackOuterContainer
            .getComponent(TransformComponent)
            .position.set(0, -95);

        const overlay = system.animator.getChildByName('overlay');

        StartAnimation(overlay, 'OverlayFadeIn');

        const arrow = GameObject.Instantiate(Arrow, {
            relativePosition: new Point(0.5, 0),
        });

        const text = GameObject.Instantiate(Text, {
            text: Game.locale.getText('Tutorial1'),
            style: {
                ...Game.style.getStyle('ftux.text'),
                wordWrapWidth: 950,
            },
            anchor: new Point(0.5, 0.5),
            position: new Point(0, 155),
            relativePosition: new Point(0.5, 0.5),
        });
        text.getComponent(TextComponent).alpha = 0;
        text.addComponent(AnimationComponent, {
            playOnStart: true,
            clipName: 'TextPopIn',
        });
        system.scene.emit(
            RenderSystemEvent.BRING_TO_FRONT,
            text
        );

        system.animator.addChild(arrow);
        system.animator.addChild(text);

        const arrowTransform = arrow.getComponent(TransformComponent);

        let currentCard: GameObject;
        let targetPosition = new Point();
        let targetRotation = 0;

        while (true) {
            // prettier-ignore
            const matches = CardStateHelper.getCardMatches(system.state.getState());

            // No matches left - hide arrow sprite and end co-routine.
            if (!matches[0]) {
                arrow.getComponent(Renderable).visible = false;
                text.getComponent(TextComponent).visible = false;
                break;
            }

            // prettier-ignore
            const card = system.getCardByID(matches[0]);

            if (card && currentCard !== card) {
                // Cache transform.
                const targetTransform = card.getComponent(TransformComponent);

                targetRotation = targetTransform.rotation;

                // prettier-ignore
                targetPosition.set(
                    targetTransform.position.x - Math.cos(targetRotation + Math.PI * 0.5) * 200,
                    targetTransform.position.y - Math.sin(targetRotation + Math.PI * 0.5) * 200
                );

                // First card.
                if (!currentCard) {
                    arrowTransform.position.set(
                        targetPosition.x,
                        targetPosition.y
                    );
                    arrowTransform.rotation = targetRotation;
                }

                currentCard = card;
            }

            // prettier-ignore
            arrowTransform.position.x += (targetPosition.x - arrowTransform.position.x) * 0.2;
            // prettier-ignore
            arrowTransform.position.y += (targetPosition.y - arrowTransform.position.y) * 0.2;
            // prettier-ignore
            arrowTransform.rotation += (targetRotation - arrowTransform.rotation) * 0.2;

            yield;
        }
    }

    private static *cardDeck(system: CardSystem): IterableIterator<number> {
        const text = GameObject.Instantiate(Text, {
            text: Game.locale.getText('Tutorial2'),
            style: {
                ...Game.style.getStyle('ftux.text'),
                fontSize: 30,
                wordWrapWidth: 250,
            },
            anchor: new Point(0.5, 0.5),
            position: new Point(-330, -115),
            relativePosition: new Point(0.5, 1),
            bounds: new Point(250, 600),
        });
        text.getComponent(TextComponent).alpha = 0;
        text.addComponent(AnimationComponent, {
            playOnStart: true,
            clipName: 'TextPopIn',
        });
        system.scene.emit(
            RenderSystemEvent.BRING_TO_FRONT,
            text
        );

        const arrow = GameObject.Instantiate(Arrow, {
            relativePosition: new Point(0.5, 1),
            position: new Point(-150, -300),
        });

        system.animator.addChild(arrow);
        system.animator.addChild(text);

        while (true) {
            // prettier-ignore
            const matches = CardStateHelper.getCardMatches(system.state.getState());
            if (matches.length === 0) {
                arrow.getComponent(Renderable).visible = true;
            } else {
                arrow.getComponent(Renderable).visible = false;
            }

            const cardsInStack = system.getCardsInStack();
            if (cardsInStack.length === 0) {
                arrow.getComponent(Renderable).visible = false;
                text.getComponent(TextComponent).visible = false;
            } else {
                text.getComponent(TextComponent).visible = true;
            }

            const cardsInPlay = system.getCardsInPlay();
            if (cardsInPlay.length === 0) {
                arrow.getComponent(Renderable).visible = false;
                text.getComponent(TextComponent).visible = false;
                break;
            }

            yield;
        }
    }

    private static *bonusStreak(system: CardSystem): IterableIterator<number> {
        const text = GameObject.Instantiate(Text, {
            text: Game.locale.getText('Tutorial3'),
            style: {
                ...Game.style.getStyle('ftux.text'),
                fontSize: 28,
                wordWrapWidth: 600,
            },
            anchor: new Point(0.5, 0.5),
            position: new Point(425, 140),
            relativePosition: new Point(0, 0),
        });
        text.getComponent(TextComponent).alpha = 0;
        text.addComponent(AnimationComponent, {
            playOnStart: true,
            clipName: 'TextPopIn',
        });
        system.scene.emit(
            RenderSystemEvent.BRING_TO_FRONT,
            text
        );

        system.animator.addChild(text);

        while (true) {
            const cardsInPlay = system.getCardsInPlay();
            if (cardsInPlay.length === 0) {
                text.getComponent(TextComponent).visible = false;
                break;
            }

            yield;
        }
    }

    private static *undoBooster(system: CardSystem): IterableIterator<number> {
        const text = GameObject.Instantiate(Text, {
            text: Game.locale.getText('TutorialUndoBooster'),
            style: {
                ...Game.style.getStyle('ftux.text'),
                fontSize: 32,
                wordWrapWidth: 325,
            },
            anchor: new Point(0.5, 0.5),
            position: new Point(-200, -100),
            relativePosition: new Point(1, 1),
        });
        text.getComponent(TextComponent).alpha = 0;
        text.addComponent(AnimationComponent, {
            playOnStart: true,
            clipName: 'TextPopIn',
        });
        system.scene.emit(
            RenderSystemEvent.BRING_TO_FRONT,
            text
        );

        system.animator.addChild(text);

        while (true) {
            const cardsInPlay = system.getCardsInPlay();
            if (cardsInPlay.length === 0) {
                text.getComponent(TextComponent).visible = false;
                break;
            }

            yield;
        }
    }

    private static *jokerBooster(system: CardSystem): IterableIterator<number> {
        const text = GameObject.Instantiate(Text, {
            text: Game.locale.getText('TutorialJokerBooster'),
            style: {
                ...Game.style.getStyle('ftux.text'),
                fontSize: 32,
                wordWrap: false,
            },
            anchor: new Point(1, 0.5),
            position: new Point(-10, -200),
            relativePosition: new Point(1, 1),
        });
        text.getComponent(TextComponent).alpha = 0;
        text.addComponent(AnimationComponent, {
            playOnStart: true,
            clipName: 'TextPopIn',
        });
        system.scene.emit(
            RenderSystemEvent.BRING_TO_FRONT,
            text
        );

        system.animator.addChild(text);

        while (true) {
            const cardsInPlay = system.getCardsInPlay();
            if (cardsInPlay.length === 0) {
                text.getComponent(TextComponent).visible = false;
                break;
            }

            yield;
        }
    }

    private static *bombCard(system: CardSystem): IterableIterator<number> {
        const text = GameObject.Instantiate(Text, {
            text: Game.locale.getText('Tutorial4'),
            style: {
                ...Game.style.getStyle('ftux.text'),
                fontSize: 30,
                wordWrapWidth: 900,
            },
            anchor: new Point(0.5, 0.5),
            position: new Point(0, -240),
            relativePosition: new Point(0.5, 1),
        });
        text.getComponent(TextComponent).alpha = 0;
        text.addComponent(AnimationComponent, {
            playOnStart: true,
            clipName: 'TextPopIn',
        });
        system.scene.emit(
            RenderSystemEvent.BRING_TO_FRONT,
            text
        );

        system.animator.addChild(text);

        while (true) {
            const cardsInPlay = system.getCardsInPlay();
            if (cardsInPlay.length === 0) {
                text.getComponent(TextComponent).visible = false;
                break;
            }

            yield;
        }
    }

    private static *suitcaseFlow(
        system: CardSystem,
        booster: number
    ): IterableIterator<number> {
        system.scene.emit('SuitcaseFTUX', booster);
    }

    private static *goldCards(system: CardSystem): IterableIterator<number> {
        const text = GameObject.Instantiate(Text, {
            text: Game.locale.getText('TutorialGoldCards'),
            style: {
                ...Game.style.getStyle('ftux.text'),
                fontSize: 28,
                wordWrapWidth: 1000,
            },
            anchor: new Point(0.5, 0.5),
            position: new Point(0, 550),
            relativePosition: new Point(0.5, 0),
        });
        text.getComponent(TextComponent).alpha = 0;
        text.addComponent(AnimationComponent, {
            playOnStart: true,
            clipName: 'TextPopIn',
        });
        system.scene.emit(
            RenderSystemEvent.BRING_TO_FRONT,
            text
        );

        system.animator.addChild(text);

        while (true) {
            const cardState = system.state.getState().levelState.cardsState;
            const goldCardsCount = getGoldCardsInPlay(cardState);
            if (goldCardsCount === 0) {
                text.getComponent(TextComponent).visible = false;
                break;
            }

            yield;
        }
    }
}
